import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth-store';
import { canOpenPath, landingPathFor } from '@/config/route-access';
import { NoAccessPage } from '@/pages/NoAccessPage';

/**
 * The one gate in front of every page of the console: a session, then the
 * permission the menu declares for the URL being opened.
 *
 * The sidebar hides what a role may not use, but a typed URL, a bookmark or a
 * link in a chat message skips the sidebar — and the page behind it rendered a
 * shell whose every request came back 403. A refused operator is sent to the
 * first menu entry they DO hold instead.
 *
 * `useAuth` is taken rather than the store's `hasPermission` on its own: that
 * function's identity never changes, so a component selecting only it would not
 * re-render when the boot-time permission probe answers. `useAuth` subscribes
 * to the user, which is what actually moves.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, hasPermission } = useAuth();
  const subjectKind = useAuthStore((s) => s.subjectKind);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (subjectKind === 'partner') return <Navigate to="/partner" replace />;

  if (!canOpenPath(location.pathname, hasPermission)) {
    const landing = landingPathFor(hasPermission);
    // Rendered in place rather than navigated to. The address stays on the page
    // that was asked for, so a permission snapshot arriving after the first
    // paint reveals it, and a landing target that is itself refused — which is
    // the only shape a redirect loop can take here — stops at one screen.
    if (landing === null || landing === location.pathname) return <NoAccessPage />;
    return <Navigate to={landing} replace />;
  }

  return <>{children}</>;
}
